// The scenario screen's own shapes, on the kit.
//
// Three rules the screen is built on, unchanged from `ScenarioScreen.css`:
//
//   1. **Every option is a square.** One shape for a difficulty, an alliance
//      and a board, so the screen reads as a set of things to choose between
//      rather than as a form — and a square is the shape the game is made of.
//   2. **No explanation is on screen twice.** Every hint is a `HoverCard`;
//      what is printed is the name of a thing and its current value.
//   3. The two knobs nobody needs are folded away.
//
// The two sizes below were `--ui-option` and `--ui-board-min`. They are local
// constants because they are *this screen's* shape rather than the language's —
// nothing else in the app has a 76px option square.

import type { ReactNode } from "react";

import { HoverCard, Select, motion, styled } from "../kit";

/** The option square's side, in px. */
const OPTION = 76;
/** The narrowest a board square may be laid out at, in px. */
const BOARD_MIN = 132;

/**
 * A label and its control, stacked.
 *
 * Stacked, not side by side: an option is 76px tall, so a label beside one is a
 * word floating in the middle of a lot of nothing. The hint hangs off the
 * *label* and is marked with a dotted underline — unmarked, an explanation is
 * something nobody discovers.
 *
 * `plain` is for a control that is not a row of squares — a select, a slider,
 * the board grid — which want no flex row wrapped round them.
 */
export function Setting({
  label,
  hint,
  children,
  plain = false
}: {
  label: string;
  hint: string;
  children: ReactNode;
  plain?: boolean;
}) {
  return (
    <div>
      {/* `top-start`, because the control this labels is directly underneath
          it — a card opening downward would cover the thing being explained. */}
      <HoverCard placement="top-start" card={hint} maxWidth={300}>
        <SettingLabel>{label}</SettingLabel>
      </HoverCard>
      {plain ? children : <Options>{children}</Options>}
    </div>
  );
}

const SettingLabel = styled("span")(({ theme }) => ({
  display: "inline-block",
  marginBottom: 6,
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  fontWeight: 700,
  color: theme.kit.color.inkDim,
  borderBottom: `1px dotted ${theme.kit.color.lineHi}`,
  cursor: "help"
}));

const Options = styled("div")({ display: "flex", flexWrap: "wrap", gap: 6 });

/**
 * One option, as a square.
 *
 * The selected one is *filled* rather than merely outlined: at this size an
 * outline change is a one-pixel difference across a 76px box, which is not
 * something you can see out of the corner of your eye while reading somewhere
 * else on the screen.
 */
const OptionSquare = styled("button")(({ theme }) => {
  const c = theme.kit.color;
  return {
    width: OPTION,
    height: OPTION,
    display: "grid",
    placeItems: "center",
    padding: 6,
    border: `2px solid ${c.line}`,
    borderRadius: theme.kit.radius.md,
    background: "transparent",
    color: c.inkDim,
    fontFamily: theme.kit.font.body,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.25,
    textAlign: "center",
    wordBreak: "break-word",
    cursor: "pointer",
    ...motion([], ["background", "border-color", "color"]),
    "&:hover": { borderColor: c.lineHi, color: c.ink },
    '&[aria-pressed="true"]': {
      background: c.brand,
      borderColor: c.brand,
      color: c.onBrand
    },
    "&:focus-visible": { outline: `2px solid ${c.brand}`, outlineOffset: 2 }
  };
});

export function Choice({
  label,
  hint,
  on,
  onClick,
  testid
}: {
  label: string;
  hint: string;
  on: boolean;
  onClick(): void;
  testid: string;
}) {
  return (
    // A row of squares, so the card opens below them and never onto a sibling.
    <HoverCard placement="bottom" card={hint} maxWidth={300}>
      <OptionSquare data-testid={testid} aria-pressed={on} onClick={onClick}>
        {label}
      </OptionSquare>
    </HoverCard>
  );
}

/** The board grid. A wide column gives bigger squares, not more whitespace. */
export const Boards = styled("div")({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fill, minmax(${BOARD_MIN}px, 1fr))`,
  gap: 8
});

const BoardSquare = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "run"
})<{ run?: boolean }>(({ theme, run }) => {
  const c = theme.kit.color;
  // The run is not a board, and is marked apart rather than hidden among them.
  const lit = run ? c.cream : c.brand;
  return {
    aspectRatio: "1",
    display: "grid",
    placeItems: "center",
    padding: 8,
    border: `2px ${run ? "dashed" : "solid"} ${c.line}`,
    borderRadius: theme.kit.radius.md,
    background: "transparent",
    color: run ? c.cream : c.ink,
    fontFamily: theme.kit.font.body,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.3,
    textAlign: "center",
    wordBreak: "break-word",
    cursor: "pointer",
    ...motion([], ["background", "border-color"]),
    "&:hover": { borderColor: c.lineHi },
    '&[aria-pressed="true"]': {
      background: lit,
      borderColor: lit,
      color: run ? c.onCream : c.onBrand
    },
    "&:focus-visible": { outline: `2px solid ${c.brand}`, outlineOffset: 2 }
  };
});

/**
 * One board, as a square.
 *
 * Square rather than a card with a paragraph in it: what these are is a set of
 * things to choose between, and the sentence that distinguishes them is worth
 * exactly one read.
 */
export function BoardTile({
  testid,
  name,
  hint,
  on,
  onClick,
  run = false
}: {
  testid: string;
  name: string;
  hint: string;
  /** Chosen. A tile is a selection now — `scenario-start` is what plays it. */
  on: boolean;
  onClick(): void;
  run?: boolean;
}) {
  return (
    <HoverCard placement="bottom" card={hint} maxWidth={300}>
      <BoardSquare
        data-testid={testid}
        run={run}
        // The same `aria-pressed` a `Choice` carries, because it is the same
        // kind of control: one of a set, one of them on.
        aria-pressed={on}
        onClick={onClick}
      >
        {name}
      </BoardSquare>
    </HoverCard>
  );
}

/**
 * A named option, or the board's own answer.
 *
 * One control for both selectors: they are the same question asked about the
 * rules and about the evaluation, and the blank entry is the half worth getting
 * right — it is the *default* in both, so it has to read as a real choice
 * rather than as an empty box.
 *
 * The kit's `Select`, which is still a native `<select>`: its dropdown is dark
 * because `index.html` sets `color-scheme: dark`, and a native one keeps the
 * keyboard and mobile behaviour nothing here would want to reimplement.
 */
export function Picker({
  testid,
  label,
  value,
  onChange,
  blank,
  options
}: {
  testid: string;
  label: string;
  value: string;
  onChange(value: string): void;
  /**
   * The empty entry, where there is one. Omitted by the team picker, and the
   * distinction is real: "leave it to the board" is a genuine answer about an
   * army style, and there is no such answer about which team you are on —
   * every army is on one, and being alone on it is the free-for-all.
   */
  blank?: string;
  options: readonly { id: string; name: string }[];
}) {
  return (
    <CellSelect
      data-testid={testid}
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {blank !== undefined && <option value="">{blank}</option>}
      {options.map((entry) => (
        <option key={entry.id} value={entry.id}>
          {entry.name}
        </option>
      ))}
    </CellSelect>
  );
}

/** A table cell's select is tighter than a stacked control's. */
const CellSelect = styled(Select)({
  padding: "5px 26px 5px 9px",
  fontSize: 12.5,
  fontWeight: 700
});

/** The army table: one row per army, one column per question. */
export const ArmyTable = styled("div")(() => ({
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  gap: "6px 8px",
  alignItems: "center"
}));

/** A column heading. Marked when it explains itself, like every hint here. */
export const ArmyHead = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim,
  "&:not(:empty)": {
    borderBottom: `1px dotted ${theme.kit.color.line}`,
    cursor: "help"
  }
}));

export const ArmySide = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  gap: 5,
  paddingRight: 4,
  fontFamily: theme.kit.font.body,
  fontSize: 13,
  fontWeight: 700,
  color: theme.kit.color.ink,
  whiteSpace: "nowrap",
  cursor: "help"
}));

/** Whose seat it is, which is the one row with nothing to set. */
export const ArmyYou = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".06em",
  textTransform: "uppercase",
  color: theme.kit.color.brand
}));
